import { useEffect, useState } from "react";
import { Award, ExternalLink, TrendingUp, Gift, Layers, Users, Plus, Check, Search, CreditCard, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { SITE } from "../lib/site";
import { useAuth } from "../context/AuthContext";
import { Pill } from "./common";

const ALLIANCE_TONE = { "Star Alliance": "amber", oneworld: "blue", SkyTeam: "green", Independent: "muted" };

export default function AiLoyaltyOverview({ code, name }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [cards, setCards] = useState({ items: [], disclosure: "" });
  const [err, setErr] = useState(false);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setData(null); setErr(false); setAdded(false); setCards({ items: [], disclosure: "" });
    api.get(`/loyalty/programs/${code}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [code]);

  useEffect(() => {
    if (data?.airline_id) api.get(`/cards?airline_id=${data.airline_id}`).then((r) => setCards(r.data)).catch(() => {});
  }, [data?.airline_id]);

  const addToHub = async () => {
    if (!user) { toast.error("Sign in to track this program in your Travel Hub"); return; }
    setAdding(true);
    try {
      await api.post("/loyalty/records", {
        airline_id: data.airline_id, airline_name: data.airline_name,
        airline_code: data.airline_code, program_name: data.program_name,
      });
      setAdded(true);
      toast.success(`${data.program_name || name} added to your Travel Hub`, {
        description: "Open My Travel Hub → My Loyalty & Mileage to add your number & balance.",
      });
    } catch { toast.error("Could not add to your Travel Hub"); }
    setAdding(false);
  };

  if (err) return <p className="text-sm text-muted-foreground" data-testid="loyalty-overview-error">Loyalty program information isn't available for this airline yet.</p>;
  if (!data) return (
    <div className="border border-zinc-300 bg-white p-8 text-center" data-testid="loyalty-overview-loading">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
      <p className="text-sm text-muted-foreground">We are compiling the loyalty & mileage guide for {name}. This can take a few seconds.</p>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="loyalty-overview">
      <div className="border border-zinc-300 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-6 h-6 text-amber-500" />
              <h3 className="font-heading font-900 text-2xl text-amber-600">{data.program_name || "Loyalty program"}</h3>
            </div>
            {data.tagline && <p className="text-muted-foreground max-w-xl">{data.tagline}</p>}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {data.alliance && <Pill tone={ALLIANCE_TONE[data.alliance] || "muted"}>{data.alliance}</Pill>}
              {data.source === "curated+ai" && <Pill tone="green">Verified source</Pill>}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {data.official_link && (
              <a href={data.official_link} target="_blank" rel="noreferrer" data-testid="loyalty-official-link"
                className="inline-flex items-center gap-1.5 bg-amber-500 text-white px-4 py-2.5 text-sm font-600 hover:bg-amber-600 transition-colors">
                <ExternalLink className="w-4 h-4" /> Official program site
              </a>
            )}
            <button onClick={addToHub} disabled={adding || added} data-testid="loyalty-add-to-hub"
              className="inline-flex items-center gap-1.5 border border-primary text-primary px-4 py-2.5 text-sm font-600 hover:bg-blue-50 disabled:opacity-60 transition-colors">
              {added ? <><Check className="w-4 h-4" /> Added to Travel Hub</> : <><Plus className="w-4 h-4" /> Add to Travel Hub</>}
            </button>
          </div>
        </div>
        {data.overview && (
          <div className="mt-5 space-y-3 text-base leading-relaxed text-zinc-700">
            {data.overview.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {data.how_to_earn?.length > 0 && (
          <LoyaltyList icon={<TrendingUp className="w-5 h-5" />} color="text-emerald-600" title="How to earn miles" items={data.how_to_earn} testid="loyalty-earn" />
        )}
        {data.how_to_redeem?.length > 0 && (
          <LoyaltyList icon={<Gift className="w-5 h-5" />} color="text-rose-600" title="How to redeem miles" items={data.how_to_redeem} testid="loyalty-redeem" />
        )}
      </div>

      {data.tiers?.length > 0 && (
        <div data-testid="loyalty-tiers">
          <h4 className="font-heading font-900 text-blue-600 text-lg flex items-center gap-2 mb-3"><Layers className="w-5 h-5" /> Elite status tiers</h4>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.tiers.map((t, i) => (
              <div key={i} className="border border-zinc-300 bg-white p-4">
                <div className="font-heading font-700">{t.name}</div>
                {t.benefits && <div className="text-sm text-muted-foreground mt-1">{t.benefits}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.partners?.length > 0 && (
        <div data-testid="loyalty-partners">
          <h4 className="font-heading font-900 text-violet-600 text-lg flex items-center gap-2 mb-3"><Users className="w-5 h-5" /> Partner airlines</h4>
          <div className="flex flex-wrap gap-2">
            {data.partners.map((p, i) => <span key={i} className="inline-flex items-center border border-zinc-300 bg-white px-3 py-1.5 text-sm">{p}</span>)}
          </div>
        </div>
      )}

      {cards.items.length > 0 ? (
        <div data-testid="loyalty-managed-cards">
          <h4 className="font-heading font-900 text-orange-600 text-lg flex items-center gap-2 mb-1"><CreditCard className="w-5 h-5" /> {data.program_name || name} credit cards</h4>
          <p className="text-xs text-muted-foreground mb-3">Ranked by {SITE.name} Score — traveler value, not commission.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {cards.items.map((c) => (
              <div key={c.id} className="border border-zinc-300 bg-white p-4 flex flex-col" data-testid={`managed-card-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-heading font-700">{c.card_name}</div>
                    <div className="text-xs text-muted-foreground">{c.issuer}{c.card_network ? ` · ${c.card_network}` : ""}</div>
                  </div>
                  {c.annual_fee && <span className="text-xs font-mono shrink-0 bg-zinc-100 px-2 py-1">{c.annual_fee}/yr</span>}
                </div>
                <div className="mt-2 space-y-1 text-sm text-zinc-700 flex-1">
                  {c.welcome_offer && <div><span className="text-muted-foreground">Welcome: </span>{c.welcome_offer}</div>}
                  {c.airline_benefits && <div><span className="text-muted-foreground">Benefits: </span>{c.airline_benefits}</div>}
                  <div className="flex flex-wrap gap-2 pt-1">
                    {c.free_checked_bag && <Pill tone="green">Free checked bag</Pill>}
                    {c.priority_boarding && <Pill tone="blue">Priority boarding</Pill>}
                    {c.lounge_access && <Pill tone="amber">Lounge: {c.lounge_access}</Pill>}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {c.go_url ? (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${c.go_url}?page=airline-${data.airline_id}`} target="_blank" rel="noreferrer sponsored"
                      data-testid={`managed-card-offer-${c.id}`} className="inline-flex items-center gap-1.5 bg-orange-500 text-white px-3 py-2 text-xs font-600 hover:bg-orange-600 transition-colors">
                      View offer <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  ) : c.official_card_url ? (
                    <a href={c.official_card_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-zinc-300 px-3 py-2 text-xs font-600 hover:border-primary text-primary">Card details <ExternalLink className="w-3.5 h-3.5" /></a>
                  ) : null}
                  {c.go_url && c.official_card_url && (
                    <a href={c.official_card_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Official card info</a>
                  )}
                  {c.last_verified && <span className="text-xs text-muted-foreground ml-auto">Verified {c.last_verified}</span>}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3 flex items-start gap-1.5 border-t border-zinc-100 pt-3"><ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {cards.disclosure}</p>
        </div>
      ) : data.credit_cards?.length > 0 && (
        <div data-testid="loyalty-credit-cards">
          <h4 className="font-heading font-900 text-orange-600 text-lg flex items-center gap-2 mb-3"><CreditCard className="w-5 h-5" /> Co-branded credit cards</h4>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.credit_cards.map((c, i) => (
              <div key={i} className="border border-zinc-300 bg-white p-4" data-testid={`loyalty-card-guide-${i}`}>
                <div className="font-heading font-700">{c.name}</div>
                <div className="mt-2 space-y-1 text-sm text-zinc-700">
                  {c.annual_fee && <div><span className="text-muted-foreground">Annual fee: </span>{c.annual_fee}</div>}
                  {c.signup_bonus && <div><span className="text-muted-foreground">Sign-up: </span>{c.signup_bonus}</div>}
                  {c.earning && <div><span className="text-muted-foreground">Earning: </span>{c.earning}</div>}
                </div>
                {c.apply_link && (
                  <a href={c.apply_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-sm text-primary hover:underline">
                    <ExternalLink className="w-3.5 h-3.5" /> Find & apply
                  </a>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">Card offers change frequently — always verify current terms and bonuses on the issuer's official page.</p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4 text-sm">
        {data.google_link && (
          <a href={data.google_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-primary hover:underline" data-testid="loyalty-google-link">
            <Search className="w-4 h-4" /> Search the official program
          </a>
        )}
      </div>
      <p className="text-xs text-muted-foreground border-t border-zinc-200 pt-4">{data.disclaimer}</p>
    </div>
  );
}

function LoyaltyList({ icon, color, title, items, testid }) {
  return (
    <div data-testid={testid}>
      <h4 className={`font-heading font-900 text-lg flex items-center gap-2 mb-3 ${color}`}>{icon} {title}</h4>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-zinc-700">
            <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${color.replace("text-", "bg-")}`} />{it}
          </li>
        ))}
      </ul>
    </div>
  );
}
